import { action, observable, computed } from 'mobx';

import BroadcasterNotesService from 'services/BroadcasterNotesService';
import messagingService from 'services/MessagingService/MessagingService';
import {
  ALL_NOTES_RESPONSE,
  BROADCASTER_NOTE_DELETED,
  CLOSE_BROADCASTER_SEARCH,
  NEW_NOTE_CREATED,
  NOTES_LIST_RESPONSE,
  NOTE_UPDATED_OR_CREATED,
  RESET_SUCCESS_NOTE_VIEW,
  SET_BROADCASTER_NOTES_SEARCH_ERROR_VALUE,
  SET_NEW_NOTE_VIEW,
  SINGLE_NOTE_RESPONSE,
  UPDATE_ALL_NOTES,
  UPDATE_ALL_NOTES_COUNT,
} from 'services/MessagingService/EventNames';
import FirebaseStore from 'stores/FirebaseStore';
import ModerationStore from 'stores/ModerationStore';
import UserStore from 'stores/UserStore';
import { ACCESS_LEVEL_STR } from 'pages/User/AccessLevel';
import APIStates from 'enum/APIStates';


const TOTAL_ITEMS = 10000;

/**
 * @class handles state of aplication for broadcast notes and expose methods
 */
class BroadcasterNotesStore {
  constructor() {
    this.messagingService = messagingService();
    this.messagingService.on(CLOSE_BROADCASTER_SEARCH, this.setIsSearchOpen);
    this.messagingService.on(NOTES_LIST_RESPONSE, this.setNotesList);
    this.messagingService.on(SET_BROADCASTER_NOTES_SEARCH_ERROR_VALUE, this.setIsSearchErrorVisible);
    this.messagingService.on(NOTE_UPDATED_OR_CREATED, this.setSuccessIsView);
    this.messagingService.on(BROADCASTER_NOTE_DELETED, this.onDeleteNote);
    this.messagingService.on(SINGLE_NOTE_RESPONSE, this.setActiveNote);
    this.messagingService.on(SET_NEW_NOTE_VIEW, this.setNewNoteView);
    this.messagingService.on(RESET_SUCCESS_NOTE_VIEW, this.resetSuccessView);
    this.messagingService.on(ALL_NOTES_RESPONSE, this.setAllNotes);
    this.messagingService.on(UPDATE_ALL_NOTES, this.updateAllNotes);
    this.messagingService.on(UPDATE_ALL_NOTES_COUNT, this.updateAllNotesCount);
    this.messagingService.on(NEW_NOTE_CREATED, this.handleNoteCreated);
  }

  @observable isNotesDrawerOpen = false;
  @observable activeColorValue = 'blue';
  @observable notesList = [];
  @observable searchValue = '';
  @observable isSearchOpen = false;
  @observable isSearchErrorVisible = false;
  @observable isNewNoteViewVisible = false;
  @observable activeNoteInNewNoteView = {};
  @observable successViewUsers = [];
  @observable totalItems = 0;
  @observable notesOffset = 0;
  @observable totalFilteredItems = 0;
  @observable allNotes = {};
  @observable saveNoteStatus = APIStates.INITIAL;

  destructor() {
    this.messagingService.off(NOTES_LIST_RESPONSE, this.setNotesList);
    this.messagingService.off(SET_BROADCASTER_NOTES_SEARCH_ERROR_VALUE, this.setIsSearchErrorVisible);
    this.messagingService.off(NOTE_UPDATED_OR_CREATED, this.setSuccessIsView);
    this.messagingService.off(BROADCASTER_NOTE_DELETED, this.onDeleteNote);
    this.messagingService.off(SINGLE_NOTE_RESPONSE, this.setActiveNote);
    this.messagingService.off(SET_NEW_NOTE_VIEW, this.setNewNoteView);
    this.messagingService.off(CLOSE_BROADCASTER_SEARCH, this.setIsSearchOpen);
    this.messagingService.off(RESET_SUCCESS_NOTE_VIEW, this.resetSuccessView);
    this.messagingService.off(ALL_NOTES_RESPONSE, this.setAllNotes);
    this.messagingService.ff(UPDATE_ALL_NOTES, this.updateAllNotes);
    this.messagingService.off(UPDATE_ALL_NOTES_COUNT, this.updateAllNotesCount);
    this.messagingService.off(NEW_NOTE_CREATED, this.handleNoteCreated);
  }

  /**
   * Method for opening user notes drawer
   */
  @action handleNotesDrawerOpen = () => {
    ModerationStore.closeModerationPopper();
    this.isNotesDrawerOpen = true;
  };

  /**
   * Method for reseting array that contains success view for users
   */
  @action resetSuccessView = () => {
    this.successViewUsers = [];
  };

  /**
   * Method for closing user notes drawer and reseting state
   */
  @action handleDrawerClose = () => {
    this.isNewNoteViewVisible = false;
    this.isNotesDrawerOpen = false;
    this.successViewUsers = [];
    this.notesList = [];
    this.activeNoteInNewNoteView = {};
    this.searchValue = '';
    this.notesOffset = 0;
  };

  /**
   * Method for changing offset
   */
  @action setNoteOffset = (value) => {
    this.notesOffset = value;
  };

  /**
   * Method for setting new note view
   * @param {bool} - target
   */
  @action setNewNoteView = (value) => {
    this.successViewUsers = [];
    this.isSearchOpen = false;
    this.isNewNoteViewVisible = value;
    this.notesOffset = 0;
  };

  /**
   * Method for selecting color from dropwdown
   * @param {string} - value color
   */
  @action handleSelectColorChange = (value) => {
    if (this.activeColorValue !== value) {
      this.successViewUsers = [];
      this.notesList = [];
      this.setNoteOffset(0);
      this.activeColorValue = value;
    }
  };

  /**
   * Method for setting notes object
   * @param {object} - target
   */
  @action setNotesList = (value) => {
    if (this.notesOffset === 0) {
      this.notesList = value.items;
    } else {
      this.notesList = [...this.notesList, ...value.items];
    }
    this.totalFilteredItems = value.totalItems;
  };

  /**
   * Method for setting input search value
   * @param {string} - value
   */
  @action setSearchValue = (value) => {
    this.setIsSearchErrorVisible(false);
    this.searchValue = value;
  };

  /**
   * Method for opening custom search dropdown
   * @param {bool} - value
   */
  @action setIsSearchOpen = (value) => {
    this.isSearchOpen = value;
  };

  /**
   * Method for selecting note from dropwdown
   * @param {string} - target
   */
  @action handleSelectNotesItem = (target) => {
    this.searchValue = target;
    BroadcasterNotesService.getSingleUserNote(UserStore.user.username, target);
  };

  /**
   * Method for deleting note from note object
   * @param {string} - username
   */
  @action onUpdateNote = (username) => {
    this.notesList = this.notesList.filter((item) => item.peerUsername?.toLowerCase() !== username);
    this.totalFilteredItems -= 1;
  };

  @action onUpdateNoteDesc = (username) => {
    this.successViewUsers = this.successViewUsers.filter((item) => item?.toLowerCase() !== username);
  };

  /**
   * Method for seting error in custom search/dropdown
   */
  @action setIsSearchErrorVisible = (value) => {
    this.isSearchErrorVisible = value;
  };

  /**
   * Method for setting active note in new note view
   * @param {object} - value note
   */
  @action setActiveNote = (value) => {
    this.activeNoteInNewNoteView = value;
  };

  /**
   * Method for setting success view for update or created note
   * @param {string} - value username
   */
  @action setSuccessIsView = (value) => {
    this.saveNoteStatus = APIStates.LOADED;
    this.successViewUsers = [...this.successViewUsers, value];
  };

  /**
   * Method that gets emmited when the user note is deleted
   * @param {string} targetName
   */
  @action onDeleteNote = (targetName) => {
    this.totalItems -= 1;
    const deepCopy = Object.assign({}, this.allNotes);
    delete deepCopy[targetName];
    this.allNotes = deepCopy;
  };

  /**
   * Method for fetching notes or note if there is no query param
   * @param {number} - offset for fetching notes
   */
  getNotesList = (offset) => {
    BroadcasterNotesService.getNotesList(UserStore.user.username, offset, this.activeColorValue);
  };

  /**
   * Method for deleting note
   * @param {targetName} - targeted username for deleting note
   */
  @action deleteNote = (targetName, isModalOpened) => {
    this.saveNoteStatus = APIStates.PENDING;
    BroadcasterNotesService.removeNote(UserStore.user.username, targetName, isModalOpened);
  };

  /**
   * Method for fetching all notes no matther of color
   */
  getAllNotes = () => {
    BroadcasterNotesService.getAllNotes(UserStore.user.username);
  };

  /**
   * Method for fetching single note
   */
  getSingleNote = (target) => {
    BroadcasterNotesService.getSingleUserNote(UserStore.user.username, target, true);
  };

  @action updateAllNotesCount = () => {
    this.totalItems += 1;
  };

  /**
   * Method for making new note
   * @param {object} - data contains whats need to be send for updating note
   * @param {string} - note = actual description of note
   * @param {string} - color = blue or red
   * @param {string} - peerUsername = username
   */
  @action makeNewNote = (data) => {
    this.saveNoteStatus = APIStates.PENDING;
    BroadcasterNotesService.makeNewNote(UserStore.user.username, data);
  };

  /**
   * Method for setting all notes
   */
  @action setAllNotes = (data) => {
    this.allNotes = data.items.reduce((obj, item) => {
      obj[item.peerUsername] = item.color;

      return obj;
    }, {});
    this.totalItems = data.totalItems;
  };

  /**
   * Method for updating all user notes object
   */
  @action updateAllNotes = ({ peerUsername, color }) => {
    const deepCopy = Object.assign({}, this.allNotes);
    deepCopy[peerUsername] = color;
    this.allNotes = deepCopy;
  };

  /**
   * Method for updating note
   * @param {object} - data contains whats need to be send for updating note
   * @param {string} - note = actual description of note
   * @param {string} - color = blue or red
   */
  @action updateNote = (data) => {
    this.saveNoteStatus = APIStates.PENDING;
    BroadcasterNotesService.updateNote(UserStore.user.username, data);
  };

  @action handleNoteCreated = () => {
    this.saveNoteStatus = APIStates.LOADED;
  };

  /**
   * @method getter searchList - search list for custom dropdown/search
   * @returns array
   */
  @computed get searchList() {
    return FirebaseStore.sortedUsers.reduce((filtered, option) => {
      if (
        UserStore.user.username !== option.originalUsername &&
        option.accessLevel !== ACCESS_LEVEL_STR.GUEST &&
        option.originalUsername.toLowerCase().includes(this.searchValue.toLocaleLowerCase())
      ) {
        const newValue = { name: option.originalUsername };
        filtered.push(newValue);
      }

      return filtered;
    }, []);
  }

  /**
   * @method getter activeNotes - filters active notes by color
   * @returns object
   */
  @computed get activeNotes() {
    if (this.activeColorValue === 'blue') {
      return this.notesList.filter((item) => item.color === 'blue');
    }

    return this.notesList.filter((item) => item.color === 'red');

  }

  /**
   * @method getter isMaxNotes - is max length notes reached
   * @returns bool
   */
  @computed get isMaxNotes() {
    return this.totalItems === TOTAL_ITEMS;
  }

  @computed get isSaveNotePending() {
    return this.saveNoteStatus === APIStates.PENDING;
  }
}

export default new BroadcasterNotesStore();
